"use client";

import {
  useMutation,
  UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { loginApi, registerApi, getMeApi, logoutApi } from "../api/auth.api";
import { 
  AuthResponse, 
  LoginPayload, 
  RegisterPayload,
  ApiError
} from "@/types/auth.types";
import { useAuthStore } from "../store/auth.store";
import { useRouter } from "next/navigation";
import { cartApi } from "../../cart/api/cart.api";
import { useCartStore } from "@/store/cart.store";

export const authQueryKeys = {
  all: ["auth"] as const,
  user: () => [...authQueryKeys.all, "user"] as const,
};

export const useAuth = (
  options?: {
    loginOptions?: Partial<UseMutationOptions<AuthResponse, AxiosError<ApiError>, LoginPayload, unknown>>;
    registerOptions?: Partial<UseMutationOptions<AuthResponse, AxiosError<ApiError>, RegisterPayload, unknown>>;
    logoutOptions?: Partial<UseMutationOptions<void, AxiosError<ApiError>, void, unknown>>;
  }
) => {
  const setUser = useAuthStore((state) => state.setUser);
  const clearUser = useAuthStore((state) => state.clearUser);
  const router = useRouter();

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => loginApi(payload),
    onSuccess: async (data, variables, context) => {
      try {
        // After successful login (HttpOnly cookies set by backend), merge cart if guest sessionId exists
        const sessionId = useCartStore.getState().sessionId;
        if (sessionId) {
          try {
            const mergedCart = await cartApi.mergeGuestCart(sessionId);
            useCartStore.getState().setCart(mergedCart);
          } catch (mergeError) {
            console.error("Failed to merge cart after login:", mergeError);
          }
        }

        // Fetch user details
        const userData = await getMeApi();
        setUser(userData.user);
      } catch (error) {
        console.error("Failed to fetch user after login", error);
        clearUser();
      }
      (options?.loginOptions?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      console.error("Login failed:", error);
      (options?.loginOptions?.onError as any)?.(error, variables, context);
    },
    ...options?.loginOptions,
  });

  const registerMutation = useMutation({
    mutationFn: (payload: RegisterPayload) => registerApi(payload),
    onError: (error, variables, context) => {
      console.error("Registration failed:", error);
      (options?.registerOptions?.onError as any)?.(error, variables, context);
    },
    ...options?.registerOptions,
  });

  const logoutMutation = useMutation({
    mutationFn: () => logoutApi(),
    onSettled: (data, error, variables, context) => {
      clearUser();
      router.push("/");
      (options?.logoutOptions?.onSettled as any)?.(data, error, variables, context);
    },
    onError: (error, variables, context) => {
      console.error("Logout failed:", error);
      (options?.logoutOptions?.onError as any)?.(error, variables, context);
    },
    ...options?.logoutOptions,
  });

  const mutationError = loginMutation.error || registerMutation.error;
  const axiosError = mutationError as AxiosError<ApiError>;
  const errorMessage =
    axiosError?.response?.data?.message || axiosError?.message || null;

  return {
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutate,
    isLoading:
      loginMutation.isPending ||
      registerMutation.isPending ||
      logoutMutation.isPending,
    error: errorMessage,
    loginMutation,
    registerMutation,
    logoutMutation,
  };
};


